import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { getWithTokenAPI } from "../../util/Api";
import {
  FileText,
  ShieldCheck,
  RotateCcw,
  Truck,
  PhoneCall,
  Phone,
  Mail,
  MessageCircle,
  Clock,
  MapPin,
  ExternalLink,
  AlertCircle,
} from "lucide-react";
import "./LegalPage.css";

const POLICIES = [
  { id: "terms-and-conditions",     label: "Terms & Conditions",    Icon: FileText     },
  { id: "privacy-policy",           label: "Privacy Policy",        Icon: ShieldCheck  },
  { id: "cancellation-refund-policy", label: "Cancellation & Refund", Icon: RotateCcw  },
  { id: "shipping-exchange-policy", label: "Shipping & Exchange",   Icon: Truck        },
  { id: "contact-us",               label: "Contact Us",            Icon: PhoneCall    },
];

const LegalPage = () => {
  const { token } = useSelector((state) => state.auth);
  const [activePolicy, setActivePolicy] = useState(POLICIES[0].id);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchPolicy();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePolicy]);

  const fetchPolicy = async () => {
    setLoading(true);
    setData(null);
    try {
      const res = await getWithTokenAPI(token, `/${activePolicy}?type=institutions`);
      if (res?.statusCode === 200 && res.data?.length > 0) {
        const item = res.data.find((d) => d.type === "institutions") || res.data[0];
        setData(item);
      }
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  const ContactRow = ({ icon: Icon, label, value, color }) => (
    <div className="contact-card">
      <div className="contact-icon-wrap" style={{ background: color + "18" }}>
        <Icon size={20} color={color} strokeWidth={1.8} />
      </div>
      <div>
        <p className="contact-label">{label}</p>
        <p className="contact-value">{value}</p>
      </div>
    </div>
  );

  const renderContactCard = (item) => (
    <div className="contact-info-grid">
      {item.phone     && <ContactRow icon={Phone}         label="Phone"         value={item.phone}        color="#2563eb" />}
      {item.email     && <ContactRow icon={Mail}          label="Email"         value={item.email}        color="#7c3aed" />}
      {item.whatsapp  && <ContactRow icon={MessageCircle} label="WhatsApp"      value={item.whatsapp}     color="#16a34a" />}
      {item.workingHours && <ContactRow icon={Clock}      label="Working Hours" value={item.workingHours} color="#d97706" />}
      {item.address && (
        <div className="contact-card full-width">
          <div className="contact-icon-wrap" style={{ background: "#ef444418" }}>
            <MapPin size={20} color="#ef4444" strokeWidth={1.8} />
          </div>
          <div>
            <p className="contact-label">Address</p>
            <p className="contact-value">{item.address}</p>
          </div>
        </div>
      )}
      {item.mapLink && (
        <div className="contact-card full-width map-btn-card">
          <a href={item.mapLink} target="_blank" rel="noopener noreferrer" className="map-link">
            <ExternalLink size={16} strokeWidth={2} />
            View on Google Maps
          </a>
        </div>
      )}
    </div>
  );

  const activePolicyObj = POLICIES.find((p) => p.id === activePolicy);

  return (
    <div className="legal-container">
      <div className="legal-header">
        <h2 className="legal-title">Legal &amp; Policies</h2>
        <p className="legal-subtitle">
          View all policies and legal information for your institution
        </p>
      </div>

      <div className="legal-layout">
        {/* Left sidebar tabs */}
        <div className="legal-tabs">
          {POLICIES.map(({ id, label, Icon }) => (
            <button
              key={id}
              className={`legal-tab-btn ${activePolicy === id ? "active" : ""}`}
              onClick={() => setActivePolicy(id)}
            >
              <span className="tab-icon-wrap">
                <Icon size={18} strokeWidth={1.8} />
              </span>
              <span className="tab-label">{label}</span>
            </button>
          ))}
        </div>

        {/* Right content area */}
        <div className="legal-content">
          {loading ? (
            <div className="legal-loading">
              <div className="spinner" />
              <p>Loading...</p>
            </div>
          ) : data ? (
            <div className="legal-doc">
              <div className="legal-doc-header">
                <div className="legal-doc-title-row">
                  {activePolicyObj && (
                    <span className="legal-doc-icon">
                      <activePolicyObj.Icon size={22} strokeWidth={1.8} color="#2563eb" />
                    </span>
                  )}
                  <h3 className="legal-doc-title">{data.title || data.heading}</h3>
                </div>
                <span className="legal-badge">Institutions</span>
              </div>

              {activePolicy === "contact-us" ? (
                renderContactCard(data)
              ) : (
                <div
                  className="legal-doc-content"
                  dangerouslySetInnerHTML={{ __html: data.content }}
                />
              )}
            </div>
          ) : (
            <div className="legal-empty">
              <AlertCircle size={40} color="#cbd5e1" strokeWidth={1.5} />
              <p>No data available for this policy.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LegalPage;

